import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react'
import debounce from 'lodash/debounce'
import api from '../../api'
import { UserSearchContext } from '../../services/search/UserSearchContext'

const SUGGESTION_LIMIT = 6
const DEBOUNCE_TIME = 250

/**
 * Standalone search provider for user/contact lookup (Repo Invitation modal).
 *
 * Serves a domain of its own — other people's accounts — which is why it is
 * separate from SearchProviderContainer. Swapping the user search engine
 * requires editing only this file; consumers read suggestions off
 * UserSearchContext.
 *
 * Which users are visible is decided by the API from the session, so there is
 * no key to wait for and nothing to configure here.
 *
 * Props:
 *   children {ReactNode} Rendered inside UserSearchContext.Provider.
 */
function UserSearchProviderContainer({ children }) {
  const [currentRefinement, setCurrentRefinement] = useState('')
  const [suggestions, setSuggestions] = useState([])

  // Responses can arrive out of order, so only the newest query may write.
  const latestQuery = useRef('')

  const fetchSuggestions = useMemo(() => debounce((query) => {
    api.fetch('/search/users', { query, limit: SUGGESTION_LIMIT })
      .then(({ hits }) => {
        if (latestQuery.current !== query) return
        setSuggestions(hits)
      })
      // keep the previous suggestions rather than flashing to empty
      .catch(() => {})
  }, DEBOUNCE_TIME, { leading: false }), [])

  useEffect(() => () => fetchSuggestions.cancel(), [fetchSuggestions])

  const refine = useCallback((query) => {
    setCurrentRefinement(query)
    latestQuery.current = query

    if (!query) {
      fetchSuggestions.cancel()
      setSuggestions([])
      return
    }
    fetchSuggestions(query)
  }, [fetchSuggestions])

  const value = useMemo(
    () => ({ suggestions, refine, currentRefinement }),
    [suggestions, refine, currentRefinement],
  )

  return (
    <UserSearchContext.Provider value={value}>
      {children}
    </UserSearchContext.Provider>
  )
}

export default UserSearchProviderContainer
